import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { SearchHit } from '@accounting/shared';
import { useSearch } from '../../hooks/useSearch';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/search')({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: SearchPage,
});

function HitLink({ hit, children }: { hit: SearchHit; children: React.ReactNode }) {
  const style = { textDecoration: 'none', color: 'inherit', display: 'block' } as const;
  switch (hit.type) {
    case 'invoice':
      return <Link to="/invoices/$id" params={{ id: hit.id }} style={style}>{children}</Link>;
    case 'check':
      return <Link to="/checks/$id" params={{ id: hit.id }} style={style}>{children}</Link>;
    case 'deposit':
      return <Link to="/deposits/$id" params={{ id: hit.id }} style={style}>{children}</Link>;
    case 'journal':
      return <Link to="/journal/$id" params={{ id: hit.id }} style={style}>{children}</Link>;
    case 'contact': {
      const to = hit.contactType === 'customer' ? '/customers' : hit.contactType === 'employee' ? '/employees' : '/vendors';
      return <Link to={to} style={style}>{children}</Link>;
    }
    case 'account':
      return <Link to="/accounts" style={style}>{children}</Link>;
    case 'bank_transaction':
      return <Link to="/bank/transactions" style={style}>{children}</Link>;
  }
}

function SearchPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { q } = Route.useSearch();
  const [term, setTerm] = useState(q ?? '');
  useEffect(() => setTerm(q ?? ''), [q]);

  const { data, isLoading, isError } = useSearch(companyId, q ?? '');

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Search</h1>
        <p className="muted">Pick a company in the switcher to search its records.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 12px' }}>Search</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({ to: '/search', search: { q: term.trim() || undefined } });
        }}
        style={{ marginBottom: 16, display: 'flex', gap: 8, maxWidth: 520 }}
      >
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search invoices, checks, deposits, journal, contacts, accounts…"
          autoFocus
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn-primary">Search</button>
      </form>

      {!q && <p className="muted">Type a term and press Search.</p>}
      {q && isLoading && <p className="muted">Searching…</p>}
      {q && isError && <p className="field-error">Search failed.</p>}
      {q && data && data.total === 0 && (
        <div className="card" style={{ padding: 24 }}>
          <p className="muted" style={{ margin: 0 }}>No matches for “{q}”.</p>
        </div>
      )}

      {data && data.groups.map((group) => (
        <div key={group.type} style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>
            {group.label} <span className="muted">({group.hits.length})</span>
          </h2>
          <div className="card" style={{ padding: 0 }}>
            {group.hits.map((hit, i) => (
              <HitLink key={hit.id} hit={hit}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 14px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {hit.title}
                    </div>
                    {hit.subtitle && (
                      <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {hit.subtitle}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {hit.amount && <div style={{ fontWeight: 500 }}>{formatCurrency(hit.amount)}</div>}
                    {hit.date && <div className="muted" style={{ fontSize: 12 }}>{formatAccountingDate(hit.date)}</div>}
                  </div>
                </div>
              </HitLink>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
